<template>
  <div class="hotels-landing-page page-container">
    <h1>{{ $t('Find Hotels Worldwide') }}</h1>
    <p class="hotels-landing-subtitle">
      {{ $t('Search and compare hotels across destinations. Find the best rates.') }}
    </p>

    <!-- Top destinations (dynamic from API) -->
    <div v-if="topDestinations.length" class="landing-section">
      <h2>{{ $t('Top Destinations') }}</h2>
      <div class="top-destinations-grid">
        <nuxt-link
          v-for="city in topDestinations"
          :key="city.slug"
          :to="localePath(`/hotels/${city.slug}`)"
          class="top-destination-card"
        >
          <span class="top-destination-card__name">{{ city.name }}</span>
          <span class="top-destination-card__count">{{ city.hotel_count }} {{ $t('hotels') }}</span>
        </nuxt-link>
      </div>
    </div>

    <!-- Browse by country -->
    <div v-if="countries.length" class="landing-section">
      <h2>{{ $t('Browse Hotels by Country') }}</h2>
      <div class="countries-list">
        <nuxt-link
          v-for="country in countries"
          :key="country.slug"
          :to="localePath(`/hotels/destinations/${country.slug}`)"
          class="country-link"
        >
          {{ country.country_name }}
          <span class="country-link__count">({{ country.hotel_count }})</span>
        </nuxt-link>
      </div>
      <div class="browse-all">
        <nuxt-link :to="localePath('/hotels/destinations')" class="browse-all-link">
          {{ $t('View all destinations') }} &rarr;
        </nuxt-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useHreflangTags } from '~/composables/useSeo'

const { $siteConfig } = useNuxtApp()
const config = useRuntimeConfig()
const localePath = useLocalePath()

const brand = computed(() => $siteConfig?.value?.brand || 'Tripening')
const baseUrl = config.public.baseURL

interface City {
  name: string
  slug: string
  hotel_count: number
}

interface Country {
  country_code: string
  country_name: string
  slug: string
  hotel_count: number
  top_cities: City[]
}

const countries = ref<Country[]>([])
const topDestinations = ref<City[]>([])

onMounted(async () => {
  try {
    const data = await $fetch<Country[]>(`${baseUrl}/api/product/v1/hotels/destinations`)
    countries.value = data || []
    // Flatten top cities from all countries, sorted by hotel count
    const allCities: City[] = (data || []).flatMap((c: Country) => c.top_cities || [])
    allCities.sort((a, b) => b.hotel_count - a.hotel_count)
    topDestinations.value = allCities.slice(0, 12)
  } catch {
    // Non-critical
  }
})

useHead({
  title: computed(() => `Find Hotels Worldwide - Best Rates & Deals | ${brand.value}`),
  link: [
    { rel: 'canonical', href: `${baseUrl}/hotels` }
  ],
  meta: [
    {
      name: 'description',
      content: computed(() => `Search and compare hotels worldwide on ${brand.value}. Find the best rates, verified reviews, and no hidden fees.`)
    },
    { property: 'og:title', content: computed(() => `Find Hotels Worldwide | ${brand.value}`) },
    { property: 'og:description', content: computed(() => `Search and compare hotels worldwide on ${brand.value}. Find the best rates.`) },
    { property: 'og:url', content: `${baseUrl}/hotels` },
    { property: 'og:image', content: `${baseUrl}/images/og/hotels-landing.jpg` },
    { property: 'og:type', content: 'website' },
    { name: 'twitter:card', content: 'summary_large_image' }
  ]
})

// Add hreflang tags for multi-language SEO
useHreflangTags('/hotels')

// Add WebSite structured data with SearchAction for hotel search
useJsonld(() => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  'name': brand.value,
  'url': `${baseUrl}/hotels`,
  'potentialAction': {
    '@type': 'SearchAction',
    'target': {
      '@type': 'EntryPoint',
      'urlTemplate': `${baseUrl}/hotels/{city}`
    },
    'query-input': 'required name=city'
  }
}))

definePageMeta({
  layout: 'search'
})
</script>

<style scoped>
.hotels-landing-page {
  padding: 40px 0 60px;
}

.hotels-landing-page h1 {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
}

.hotels-landing-subtitle {
  font-size: 16px;
  color: var(--dark-grey);
  margin-bottom: 40px;
}

.landing-section {
  margin-bottom: 40px;
}

.landing-section h2 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
}

.top-destinations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
}

.top-destination-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border: 1px solid var(--light-grey-border, #e0e0e0);
  border-radius: 10px;
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.15s, transform 0.15s;
}

.top-destination-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

.top-destination-card__name {
  font-size: 15px;
  font-weight: 600;
}

.top-destination-card__count {
  font-size: 13px;
  color: var(--dark-grey);
  white-space: nowrap;
}

.countries-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
}

.country-link {
  font-size: 15px;
  font-weight: 500;
  color: var(--blue, #1985f9);
  text-decoration: none;
  padding: 4px 0;
}

.country-link:hover {
  text-decoration: underline;
}

.country-link__count {
  font-weight: 400;
  color: var(--dark-grey);
  font-size: 13px;
}

.browse-all {
  margin-top: 16px;
}

.browse-all-link {
  color: var(--blue, #1985f9);
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
}

.browse-all-link:hover {
  text-decoration: underline;
}

@media screen and (max-width: 768px) {
  .hotels-landing-page h1 {
    font-size: 22px;
  }

  .top-destinations-grid {
    grid-template-columns: 1fr;
  }
}
</style>
